import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { NavBar } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { HelpCircle, Send, CheckCircle, Loader2, MessageCircle, Heart } from "lucide-react";

export default function HelpPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${API}/support/tickets`, {
        method: "POST",
        headers,
        body: JSON.stringify({ name: name.trim(), email: email.trim(), message: message.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setTicketId(data.ticket_id);
        toast.success("Message sent successfully!");
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewMessage = () => {
    setMessage("");
    setTicketId(null);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <SEO
        title="Help & Support"
        description="GottaSecond help center. Get support for your account, job postings, hiring, contracts, and more."
        path="/help"
      />
      <NavBar />

      <main className="flex-1 max-w-2xl mx-auto px-6 py-16 w-full">
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-[#FF6B6B]/10 flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8 text-[#FF6B6B]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="help-title">How Can We Help?</h1>
          <p className="text-white/60 text-base leading-relaxed max-w-lg mx-auto">
            We're devoted to building the best platform for photography professionals. 
            Your feedback helps us improve — we'd love to hear your suggestions, questions, or anything on your mind.
          </p>
        </div>

        {ticketId ? (
          <Card className="bg-white/[0.03] border-white/10 p-8 text-center" data-testid="ticket-confirmation">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Message Received!</h2>
            <p className="text-white/60 mb-4">Your ticket number is:</p>
            <div className="inline-block px-6 py-3 bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 rounded-sm mb-6">
              <span className="text-2xl font-bold text-[#FF6B6B] tracking-wider" data-testid="ticket-number">{ticketId}</span>
            </div>
            <p className="text-white/50 text-sm mb-8">
              We've sent a confirmation email to <strong className="text-white/70">{email}</strong>. 
              Our team will review your message and get back to you as soon as possible.
            </p>
            <Button onClick={handleNewMessage} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="send-another-btn">
              <MessageCircle className="w-4 h-4 mr-2" />Send Another Message
            </Button>
          </Card>
        ) : (
          <Card className="bg-white/[0.03] border-white/10 p-8" data-testid="help-form-card">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Your Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="help-name-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email Address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="help-email-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Your Message</label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us what's on your mind — feedback, suggestions, questions, or anything else..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 min-h-[150px]"
                  data-testid="help-message-input"
                  required
                  maxLength={2000}
                />
                <p className="text-xs text-white/30 mt-1 text-right">{message.length}/2000</p>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-bold py-3"
                data-testid="help-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send Message</>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="w-full border-white/20 text-white/60 hover:bg-white/10"
                data-testid="help-cancel-btn"
              >
                Cancel
              </Button>
            </form>
          </Card>
        )}

        <div className="text-center mt-12">
          <div className="flex items-center justify-center gap-2 text-white/30 text-sm">
            <Heart className="w-4 h-4" />
            <span>Built with care for the photography community</span>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
